import React from 'react';
import PropTypes from 'prop-types';
import { List, Map } from 'immutable';
import { cardImageUrl } from '../services/helpers';
import { EnergySymbol } from '../Energy';
import { energySymbols } from '../Energy/energySymbols';
import { Count, Damage, Energies, Face, StatusBadges } from './styled';

/**
 * A card as it shows on the board or in a hand. With no card (or a face-down one) it shows the card back.
 */
const CardFace = ({
  card, label, damage, conditions, protection, hindrance, hinderedBy, trainers, conversions, energyAttachments, count,
  targetable, dim, selected,
}) => {
  const symbols = energySymbols(energyAttachments);
  const hasConditions = conditions && conditions.size > 0;
  const hasTrainers = trainers && trainers.size > 0;
  const hasConversions = conversions && conversions.size > 0;

  return (
    <Face
      style={{ backgroundImage: `url(${cardImageUrl(card)})` }}
      $targetable={targetable}
      data-targetable={targetable ? 'true' : undefined}
      $dim={dim}
      $selected={selected}
      role="img"
      aria-label={label || (card && card.get('name')) || 'Face-down card'}
    >
      {damage > 0 && <Damage>{damage}</Damage>}
      {(hasConditions || protection || hinderedBy || hasTrainers || hasConversions) && <StatusBadges>
        {hasConditions && conditions.map(name => <li key={name}>{name}</li>)}
        {protection && <li data-kind="protection" title={protection} aria-label={protection}>Protected</li>}
        {hinderedBy && <li data-kind="hindrance" title={hindrance} aria-label={`${hinderedBy}: ${hindrance}`}>{hinderedBy}</li>}
        {hasTrainers && trainers.map((name, i) => <li key={`${name}-${i}`} data-kind="trainer">{name}</li>)}
        {hasConversions && conversions.map(text => <li key={text} data-kind="conversion">{text}</li>)}
      </StatusBadges>}
      {symbols.size > 0 && <Energies>
        {symbols.map((type, i) => <EnergySymbol key={i} type={type} size="var(--energy-size)" />)}
      </Energies>}
      {count !== undefined && count !== null && <Count>{count}</Count>}
    </Face>
  );
};

CardFace.propTypes = {
  card: PropTypes.instanceOf(Map),
  /** What a screen reader calls the card, when its name is not enough (a face-down prize). */
  label: PropTypes.string,
  damage: PropTypes.number,
  /** Special conditions by name, like Asleep or Poisoned; only an active Pokémon has them. */
  conditions: PropTypes.instanceOf(List),
  /** Protection from attacks this turn, like Withdraw's, as the server describes it ("Protected from damage"). */
  protection: PropTypes.string,
  /** What an opponent's attack like Amnesia stops this turn ("Can't use Ember"), and that attack's name. */
  hindrance: PropTypes.string,
  hinderedBy: PropTypes.string,
  /** Trainers attached to the Pokémon by name, like PlusPower or Defender; one badge each, as they stack. */
  trainers: PropTypes.instanceOf(List),
  /** A Weakness or Resistance an attack changed (Conversion), like "Weak to Fighting". */
  conversions: PropTypes.instanceOf(List),
  energyAttachments: PropTypes.instanceOf(List),
  count: PropTypes.number,
  targetable: PropTypes.bool,
  dim: PropTypes.bool,
  selected: PropTypes.bool,
};

export default CardFace;
